import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';

import { contactsApi, edgeApi, syncApi } from '@lib/apiClients';
import { microServicesRoutes } from '@constants/microServicesRoutes';
import { privateRoutes } from '@constants/privateRoutesConnect';
import { logError } from '@lib/logger/logError';
import { logInfo } from '@lib/logger/logInfo';
import { logWarning } from '@lib/logger/logWarning';
import { useSessionStore } from '@features/auth';
import { useTwilioVoice } from '@hooks/useTwilioVoice';
import { useTwilioStore } from '@store/twilio/useTwilioStore';
import { playRecording, getRecordingById } from '@services/Common';
import type { PlayRecordingPayload } from '@services/Common';

import ContactCard from '@ui/shared/Dialer/ContactCard';
import IntentCard from '@ui/shared/Dialer/IntentCard';
import Dialpad from '@ui/shared/Dialer/Dialpad';
import NotesCard from '@ui/shared/Dialer/NotesCard';
import DispositionsModal from '@ui/shared/Dialer/DispositionsModal';

import SessionHeader from '../components/SessionHeader';
import SessionContactsTable from '../components/SessionContactsTable';
import {
  type CallSessionResponse,
  type ContactPreview,
  type DialingContact,
  type GetDialingStatusResponse,
  type CondenseInfoItem,
  type CondenseInfoAction,
  useWizardStore, type CallLogSDR,
} from '@features/wizard';
import { useBreadcrumbStore } from '@ui/Layout/store/useBreadcrumbStore.ts';
import ConfirmEndSessionDialog from '@features/wizard/components/ConfirmEndSessionDialog.tsx';
import { logSuccess } from '@lib/logger/logSuccess.tsx';
import useToastMessage from '@lib/logger/toast.ts';
import { useAgentApi } from '@features/agent/hooks/useAgentApi.ts';
import { type ContactInfoDto } from '@features/contacts';
import { SessionPausedBanner } from '@features/wizard/components/SessionPausedBanner.tsx';

// Polling interval for dialing status updates (3 seconds balances responsiveness and server load)
const POLL_INTERVAL = 3000;

// The incoming Twilio leg can arrive before the call log is queryable by
// conference name, so the lookup is retried across roughly 4s before the
// transfer is declared lost.
const TRANSFER_LOOKUP_ATTEMPTS = 5;
const TRANSFER_LOOKUP_RETRY_MS = 800;

// A transfer is only torn down after this many consecutive failed conference
// checks (~9s at the 3s poll). One failure means nothing: the check runs every
// 3s against a network that was demonstrably flaky during the reported incidents.
const CONFERENCE_CHECK_FAILURES_BEFORE_END = 3;

const sleep = async (ms: number): Promise<void> => {
  await new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });
};

// V1 SDR Disposition options
const DISPOSITION_OPTIONS = [
  { value: 'SA', label: 'Set Appointment' },
  { value: 'OO', label: 'Opt Out' },
  { value: 'LVM', label: 'Left Voicemail' },
  { value: 'PR', label: 'Phone Refusal (hangup)' },
  { value: 'GC', label: 'Gatekeeper Conversation' },
  { value: 'RAC', label: 'Referred to another contact' },
  { value: 'NLC', label: 'No Longer With Company' },
  { value: 'DMC', label: 'Decision Maker Conversation' },
  { value: 'WN', label: 'Wrong Number' },
  { value: 'BN/BS', label: 'Bad Number/Busy Signal' },
  { value: 'OTR', label: 'Other' },
];

interface TransferredContact extends ContactPreview {
  callId: string;
  twilioCallSid: string;
  speech?: string;
  scriptTitle?: string;
  externalId : string;
}

const getProcessedCallsKey = (sid: string): string => `processed-transfer-call-ids-${sid}`;

const getStoredProcessedCallIds = (storageKey: string): Set<string> => {
  const raw = sessionStorage.getItem(storageKey);
  if (raw == null) {
    return new Set();
  }
  try {
    return new Set(JSON.parse(raw) as string[]);
  } catch {
    return new Set();
  }
};

const storeProcessedCallIds = (storageKey: string, set: Set<string>): void => {
  sessionStorage.setItem(storageKey, JSON.stringify(Array.from(set)));
};

/**
 * Maps the action field from getDialingStatus (condenseInfo) to UI status
 * This matches v1 behavior where action values are: called, transferred, answered, voicemail, etc.
 */
const mapActionToUiStatus = (action: CondenseInfoAction): DialingContact['status'] => {
  const normalizedAction = action.toLowerCase();

  switch (normalizedAction) {
  case 'called':
    return 'called';
  case 'transferred':
    return 'transferred';
  case 'answered':
    return 'answered';
  case 'voicemail':
    return 'voicemail';
  case 'not_answered':
  case 'transfer_error':
    return 'failed';
  case 'cut_off':
    return 'cut_off';
  case 'call_ended':
    return 'called';
  default:
    return 'pending';
  }
};

/**
 * Safely coerce a value to string — prevents [object Object] in URLs
 * when MongoDB ObjectId or other non-string values leak through the API.
 */
const safeStr = (v: unknown): string => {
  if (v == null) {
    return '';
  }
  if (typeof v === 'string') {
    return v;
  }
  if (typeof v === 'number') {
    return String(v);
  }
  if (typeof v === 'object') {
    // MongoDB extended JSON: { $oid: "hexstring" }
    const rec = v as Record<string, unknown>;
    if ('$oid' in rec && typeof rec.$oid === 'string') {
      return rec.$oid;
    }
  }
  // Fallback: stringify anything, reject only "[object Object]"
  const str = String(v);
  return str === '[object Object]' ? '' : str;
};

/**
 * Maps condenseInfo item to DialingContact format
 */
const mapCondenseInfoToContact = (item: CondenseInfoItem): DialingContact => {
  const id = safeStr(item.contactId) !== '' ? safeStr(item.contactId) : (safeStr(item.id) !== '' ? safeStr(item.id) : safeStr(item._id));
  const callId = safeStr(item.id) !== '' ? safeStr(item.id) : safeStr(item._id);
  return {
    id,
    name: item.name ?? '',
    phone: item.phone ?? '',
    accountName: item.accountName,
    jobTitle: item.jobTitle,
    email: item.email,
    notes: item.notes,
    notesContact: item.notes,
    notesSdr: item.notesSDR,
    status: mapActionToUiStatus(item.action),
    callId,
    notesAgent: item.notesAgent,
  };
};

export default function SessionDashboardPage (): React.JSX.Element {
  const { t } = useTranslation();
  const { id: sessionId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentUser } = useSessionStore();
  const organizationId = currentUser?.organizationId ?? ''.trim();
  const {
    wizardResetInitialState,
    contactListId,
    outreachData,
    salesforceNonce,
    hubspotNonce,
    setIsSessionStarted
  } = useWizardStore();

  const outreachNonce = outreachData?.nonce ?? '';

  // Twilio for SDR call handling
  const sdrId = currentUser?.userId ?? undefined;
  const { endCall } = useTwilioVoice(sdrId);
  const activeCall = useTwilioStore((s) => s.activeCall);
  const storeIsMuted = useTwilioStore((s) => s.isMuted);
  const setIsMuted = useTwilioStore((s) => s.setIsMuted);
  const conferenceName = useTwilioStore((s) => s.conferenceName);

  const [, setSession] = useState<CallSessionResponse | null>(null);
  const [dialingContacts, setDialingContacts] = useState<DialingContact[]>([]);
  const [queueLength, setQueueLength] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [isEnding, setIsEnding] = useState(false);
  const [isOpenDialog, setIsOpenDialog] = useState(false);
  const [callingFromNumber, setCallingFromNumber] = useState<string>('');

  // Transfer/Dialer state
  const [isOnTransfer, setIsOnTransfer] = useState(false);
  const [transferredContact, setTransferredContact] = useState<TransferredContact | null>(null);
  const [callActive, setCallActive] = useState(false);
  const [muted, setMuted] = useState(false);
  const [notes, setNotes] = useState('');
  const [sdrNotes, setSdrNotes] = useState('');
  const [dialerKey, setDialerKey] = useState(1);

  const { saveContactNotes, invalidateSession } = useAgentApi();

  // Voicemail drop state
  const [voicemailUrl, setVoicemailUrl] = useState<string | null>(null);
  const [isOnVoicemail, setIsOnVoicemail] = useState(false);

  // Sync local muted state with Twilio SDK events (e.g. conference starts SDR muted)
  useEffect(() => {
    setMuted(storeIsMuted);
  }, [storeIsMuted]);

  // Dispositions modal
  const [showDispositions, setShowDispositions] = useState(false);
  const [disposition, setDisposition] = useState('');
  const [isSubmittingDisposition, setIsSubmittingDisposition] = useState(false);

  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const conferencePollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const conferenceCheckFailuresRef = useRef(0);
  const initializedRef = useRef(false);
  const sessionRef = useRef<CallSessionResponse | null>(null);
  const isEndingCallRef = useRef(false);
  const isSubmittingRef = useRef(false);

  const processedCallsKey = getProcessedCallsKey(sessionId ?? '');
  const processedCallIdsRef = useRef<Set<string>>(getStoredProcessedCallIds(processedCallsKey));

  // Refs that mirror state — used inside polling callbacks to avoid stale closures
  const isOnTransferRef = useRef(false);
  const transferredContactRef = useRef<TransferredContact | null>(null);
  const conferenceNameRef = useRef<string | null>(null);

  isOnTransferRef.current = isOnTransfer;
  transferredContactRef.current = transferredContact;
  conferenceNameRef.current = conferenceName;

  const { showToast } = useToastMessage();


  // Just allow a process by transfer flow
  const processingTransferRef = useRef(false);

  const setItems = useBreadcrumbStore((s) => s.setItems);
  useEffect(() => {
    setItems([
      { labelKey: 'breadcrumb.home', href: '/workspace/sessions/new' },
      { labelKey: 'breadcrumb.wizard', href: '/workspace/sessions/new' },
      { labelKey: sessionId ?? '' },
    ]);
  }, [setItems]);

  // Mute handler that actually calls Twilio SDK
  const handleToggleMute = useCallback(
    async (next: boolean) => {
      try {
        if (activeCall == null || typeof activeCall.mute !== 'function') {
          await logWarning('No active call to mute', 'SessionDashboardPage/handleToggleMute', undefined, false);
          return;
        }
        activeCall.mute(next);
        setMuted(next);
        setIsMuted(next); // Sync with Zustand store for consistency with AgentsPage
        await logInfo(next ? t('agentLayout.toasts.info.microMuted') : t('agentLayout.toasts.info.microUnmuted'), 'SessionDashboardPage/handleToggleMute', undefined, true);
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Unknown error';
        await logError(msg, 'SessionDashboardPage/handleToggleMute', undefined, false);
      }
    },
    [activeCall, setIsMuted]
  );

  // Voicemail drop: get audio duration with error fallback
  const getVMDuration = useCallback(async (url: string): Promise<number> =>
    await new Promise(resolve => {
      const audio = new Audio(url);
      audio.addEventListener('loadedmetadata', () => {
        resolve(audio.duration * 1000);
      });
      audio.addEventListener('error', () => {
        resolve(30000);
      });
    }), []);

  // Voicemail drop: play voicemail in conference
  // Uses conferenceName (conference friendlyName from transfer), NOT callSid
  // (callSid is the SDR's leg SID, different from conference friendlyName)
  const handlePlayVoicemail = useCallback(async (): Promise<void> => {
    if (voicemailUrl == null || voicemailUrl.trim() === '') {
      await logWarning('No voicemail configured for session', 'SessionDashboard/handlePlayVoicemail', undefined, false);
      return;
    }

    const confName = conferenceName ?? '';
    if (confName === '') {
      await logWarning('No active conference for voicemail', 'SessionDashboard/handlePlayVoicemail', undefined, false);
      return;
    }

    try {
      const payload: PlayRecordingPayload = {
        friendlyNameVM: confName,
        recordingUrl: voicemailUrl,
      };
      const { success } = await playRecording(payload);
      if (!success) {
        await logError(t('sdrLayout.toasts.error.playRecording'), 'SessionDashboard/handlePlayVoicemail', undefined, true);
        return;
      }

      setIsOnVoicemail(true);
      await logInfo(t('sdrLayout.toasts.info.playRecording'), 'SessionDashboard/handlePlayVoicemail');

      const duration = await getVMDuration(voicemailUrl);
      setTimeout(() => {
        setIsOnVoicemail(false);
      }, duration + 500);
    } catch (error) {
      await logError(t('sdrLayout.toasts.error.playVoicemail'), 'SessionDashboard/handlePlayVoicemail', { error }, true);
    }
  }, [voicemailUrl, conferenceName, getVMDuration]);

  const fetchSession = useCallback(async (): Promise<void> => {
    if (sessionId === undefined) {
      return;
    }

    try {
      const url = `${microServicesRoutes.contacts.get.callSession.getByIdNest}/${sessionId}`;
      const response = await contactsApi.get<{ callSession: CallSessionResponse }>(url);

      if (response.data?.callSession !== undefined) {
        const sessionData = response.data.callSession;
        setSession(sessionData);
        sessionRef.current = sessionData;
        setIsPaused(sessionData.status === 'paused');

        if (sessionData.status === 'finished') {
          navigate(`/${privateRoutes.HOME}`);
        }
      }
    } catch (error) {
      await logError(t('sdrLayout.toasts.error.fetchSession'), 'SessionDashboard/fetchSession', { error });
    }
  }, [sessionId, navigate]);

  const fetchCallingNumber = useCallback(async (): Promise<void> => {
    try {
      const url = microServicesRoutes.edge.get.twilio.currentNumber;
      const response = await edgeApi.get<string>(url);
      if (response.data !== undefined && response.data !== '') {
        setCallingFromNumber(response.data);
      }
    } catch (error) {
      // Not critical, just log
      void logWarning('Failed to fetch calling number', 'SessionDashboard/fetchCallingNumber', { error }, false);
    }
  }, []);

  // Unified polling function - fetches session info and dialing status (same as v1)
  // Uses getDialingStatus endpoint which returns condenseInfo with action field
  const fetchDialingStatus = useCallback(async (): Promise<void> => {
    if (currentUser === null || sessionId === undefined) {
      return;
    }

    // No transfer guard here on purpose. This poll is the only thing keeping the
    // session status, the queue count and the contacts table current, and it used
    // to short-circuit whenever a transfer was being processed — so a detection
    // that never finished froze the SDR's whole view. That is what made the SDR
    // read the session as active while every agent saw it paused. Transfer
    // detection is guarded on its own, in the conferenceName effect below.
    try {
      // Fetch session info and dialing status in parallel
      const [sessionResponse, dialingStatusResponse] = await Promise.all([
        // Get session data with queueLength
        contactsApi.get<{
          callSession: CallSessionResponse;
          queueLength: number;
        }>(`${microServicesRoutes.contacts.get.callSession.getByMultipleStatusAndSdrId}/${currentUser.userId}?status=started&status=paused`)
          .catch(() => ({ data: null })),
        // Get dialing status with action field (called, transferred, answered, etc.)
        contactsApi.get<GetDialingStatusResponse>(
          `${microServicesRoutes.contacts.get.callSession.getDialingStatus}/${sessionId}`
        ).catch(() => ({ data: null })),
      ]);

      // If session fetch failed (404 or other), skip processing
      if (sessionResponse.data == null) {
        return;
      }

      const sessionData = sessionResponse.data?.callSession;

      if (sessionData !== undefined && sessionData.id === sessionId) {
        setSession(sessionData);
        sessionRef.current = sessionData;
        // Prefer queueLength from dialingStatus if available, fallback to session response
        const qLength = dialingStatusResponse.data?.queueLength ?? sessionResponse.data?.queueLength ?? 0;
        setQueueLength(qLength);
        setIsPaused(sessionData.status === 'paused');

        if (sessionData.status === 'finished') {
          navigate(`/${privateRoutes.HOME}`);
          return;
        }
      }

      // Process dialing status response - uses action field (called, transferred, answered, etc.)
      const responseData = dialingStatusResponse.data;
      let contacts: DialingContact[] = [];

      if (responseData?.condenseInfo != null && Array.isArray(responseData.condenseInfo)) {
        // Map condenseInfo items to DialingContact format using action field
        contacts = responseData.condenseInfo.map(mapCondenseInfoToContact);
      }

      // Transfer detection is now handled reactively via the conferenceName useEffect
      // (immediate detection ~200-500ms instead of waiting for the next 3s poll cycle).
      // Polling only updates the contacts table; it no longer performs transfer lookups.

      setDialingContacts((prevContacts) => {
        return contacts.map(contact => {
          const prevContact = (contact.id !== '')
            ? prevContacts.find(p => p.id === contact.id)
            : undefined;

          // Persist previous final status
          if (prevContact?.finalStatus !== undefined && (
            prevContact.finalStatus === 'voicemail' || prevContact.finalStatus === 'transferred'
          )) {
            return prevContact;
          }

          contact.finalStatus = contact.status;

          return contact;
        });
      });
    } catch (error: unknown) {
      const axiosError = error as { response?: { status?: number } };
      if (axiosError.response?.status === 404) {
        setDialingContacts([]);
        setQueueLength(0);
        return;
      }
      await logError(t('sdrLayout.toasts.error.fetchDialingStatus'), 'SessionDashboard/fetchDialingStatus', { error }, true);
    }
  }, [currentUser, sessionId, navigate, processedCallsKey]);

  const handlePause = useCallback(async (): Promise<void> => {
    if (sessionId === undefined) {
      return;
    }

    try {
      const url = `${microServicesRoutes.contacts.put.callSession.updateNest}/${sessionId}`;
      await contactsApi.put(url, { status: 'paused' });
      setIsPaused(true);
      setIsSessionStarted(false);
      showToast('info', `${t('wizard.dashboard.pausedToast')}`);
      await logInfo(t('sdrLayout.toasts.info.pauseSession'), 'SessionDashboard/handlePause', { sessionId }, true);
    } catch (error) {
      await logError(t('sdrLayout.toasts.error.pauseSession'), 'SessionDashboard/handlePause', { error }, true);
    }
  }, [sessionId]);

  const handleResume = useCallback(async (): Promise<void> => {
    if (sessionId === undefined) {
      return;
    }

    try {
      const url = `${microServicesRoutes.contacts.put.callSession.updateNest}/${sessionId}`;
      await contactsApi.put(url, {
        status: 'started',
        sdrStatus: 'free',
        action: {
          action: 'started',
          userId: sdrId,
        },
      });

      setIsPaused(false);
      setIsSessionStarted(true);
      showToast('warning', `${t('wizard.dashboard.startedToast')}`);

      if (sdrId !== '') {
        try {
          const deleteSdrLogUrl = `${microServicesRoutes.contacts.delete.sdrLogs.deleteSdrLog}/${sdrId}`;
          await contactsApi.delete(deleteSdrLogUrl);
        } catch (error) {
          void logError('Failed to delete SDR log', 'SessionDashboard/handleResume', { error }, false);
        }
      }
      await logInfo(t('sdrLayout.toasts.info.resumeSession'), 'SessionDashboard/handleResume', { sessionId }, true);
    } catch (error) {
      await logError(t('sdrLayout.toasts.error.resumeSession'), 'SessionDashboard/handleResume', { error }, true);
    }
  }, [sessionId]);

  function buildSyncUrl (): string {
    let syncJobUrl;
    let nonce;
    let currentListType = 'csv';

    if (outreachNonce !== '') {
      syncJobUrl = microServicesRoutes.sync.post.outreach.updateJob;
      nonce = outreachNonce;
      currentListType = 'outreach';
    }

    if (salesforceNonce !== '') {
      syncJobUrl = microServicesRoutes.sync.post.salesforce.updateJob;
      nonce = salesforceNonce;
      currentListType = 'salesforce';
    }

    if (hubspotNonce !== '') {
      syncJobUrl = microServicesRoutes.sync.post.hubspot.updateJob;
      nonce = hubspotNonce;
      currentListType = 'hubspot';
    }
    
    if (currentListType === 'csv') {
      return '';
    }
    return `${syncJobUrl}/${nonce}/${organizationId}/${contactListId}`;
  }

  async function startSyncJob (): Promise<void> {
    const url = buildSyncUrl();

    if (url === '') {
      return;
    }

    try {
      const syncContactsResponse = await syncApi.post(url); 
      if (syncContactsResponse.data != null) {
        await logSuccess(
          `Starting contacts sync process for list: ${contactListId}`, 
          'SessionDashboardPage.startSyncJob', 
          { response: syncContactsResponse.data }, 
          false );
      }
    } catch (error) {
      await logError(`Failed to start sync job ${JSON.stringify(error)}`,
        startSyncJob.name, 
        { url }, 
        false);
      throw new Error(`Could not send sync Job: ${JSON.stringify(error)}`);
    }
  }

  const handleEndSession = useCallback(async (): Promise<void> => {
    if (sessionId === undefined) {
      return;
    }

    setIsEnding(true);
    try {
      const url = `${microServicesRoutes.contacts.put.callSession.updateNest}/${sessionId}`;
      await contactsApi.put(url, {
        status: 'finished',
        action: { action: 'finished', userId: sdrId ?? '' },
      });
      await logInfo(t('sdrLayout.toasts.info.endSession'), 'SessionDashboard/handleEndSession', { sessionId }, true);
      
      await startSyncJob();
      setIsSessionStarted(false);

      navigate(`/${privateRoutes.WIZARD}`);

      wizardResetInitialState();

      await invalidateSession(sessionId);

      navigate(`/dashboard/session/${sessionId}`);
    } catch (error) {
      await logError(t('sdrLayout.toasts.error.endSession'), 'SessionDashboard/handleEndSession', { error }, true);
      setIsEnding(false);
    }
  }, [sessionId, navigate]);

  // Cleanup function for when a call ends (used by both handleCallEnd and conference polling)
  // callLogId = condenseInfo document _id (for deduplication)
  // twilioSid = real Twilio call SID (for Twilio API calls and call-log updates)
  const executeCallCleanup = useCallback(async (callLogId: string, twilioSid: string, sdrId: string): Promise<void> => {
    const src = 'SessionDashboard/executeCallCleanup';
    void logInfo('Call cleanup started', src, { callLogId, twilioSid, sdrId }, false);

    // Mark as processed (use callLogId so subsequent polls skip this entry)
    if (callLogId !== '') {
      processedCallIdsRef.current.add(callLogId);
    }
    if (twilioSid !== '' && twilioSid !== callLogId) {
      processedCallIdsRef.current.add(twilioSid);
    }
    storeProcessedCallIds(processedCallsKey, processedCallIdsRef.current);

    setCallActive(false);
    setMuted(false); // Reset mute state for next call

    // 1. End the SDR's Twilio call locally
    try {
      endCall();
    } catch (error) {
      void logError('Failed to end local Twilio call', src, { error }, false);
    }

    // 2. Terminate the Twilio conference for ALL participants (requires real Twilio SID)
    if (twilioSid !== '') {
      try {
        const terminateUrl = `${microServicesRoutes.edge.get.twilio.terminateConference}/${twilioSid}/terminate`;
        await edgeApi.post(terminateUrl);
      } catch (error) {
        void logError('Failed to terminate conference', src, { error, twilioSid }, false);
      }
    }

    if (sessionId !== undefined) {
      // 3. Hangup the call session (updates sdrStatus in DB)
      try {
        const hangupUrl = `${microServicesRoutes.contacts.put.callSession.hangupNest}/${sessionId}`;
        await contactsApi.put(hangupUrl);
      } catch (error) {
        void logError('Failed to hangup call session', src, { error, sessionId }, false);
      }

      // 4. Delete SDR log
      if (sdrId !== '') {
        try {
          const deleteSdrLogUrl = `${microServicesRoutes.contacts.delete.sdrLogs.deleteSdrLog}/${sdrId}`;
          await contactsApi.delete(deleteSdrLogUrl);
        } catch (error) {
          void logError('Failed to delete SDR log', src, { error, sdrId }, false);
        }
      }

      // Call log should not be mutated on cleanup. The backend handles the final action.
      // 5. Free SDR status and pause session (keep paused until SDR manually resumes)
      try {
        const sessionUrl = `${microServicesRoutes.contacts.put.callSession.updateNest}/${sessionId}`;
        await contactsApi.put(sessionUrl, {
          sdrStatus: 'free',
          status: 'paused',
          action: {
            action: 'paused',
            userId: sdrId,
          },
        });
      } catch (error) {
        void logError('Failed to free SDR status', src, { error, sessionId }, false);
      }
    }

    void logInfo('Call cleanup completed', src, { callLogId, twilioSid, sdrId }, false);
    setShowDispositions(true);
  }, [sessionId, endCall, processedCallsKey]);

  const handleCallEnd = useCallback(async (): Promise<void> => {
    if (isEndingCallRef.current) {
      return;
    }
    isEndingCallRef.current = true;

    const sdrId = currentUser?.userId ?? '';
    const callLogId = transferredContact?.callId ?? '';
    const twilioSid = transferredContact?.twilioCallSid ?? '';

    await executeCallCleanup(callLogId, twilioSid, sdrId);
    showToast('info', `${t('wizard.dashboard.pausedToast')}`);
  }, [currentUser?.userId, transferredContact?.callId, transferredContact?.twilioCallSid, executeCallCleanup]);

  const handleSubmitDisposition = useCallback(async (): Promise<void> => {
    if (transferredContact === null || disposition === '') {
      return;
    }

    setDialerKey(prev => prev + 1);

    // Prevent double-click: ref guard checked before React re-renders with submitDisabled
    if (isSubmittingRef.current) {
      return;
    }
    isSubmittingRef.current = true;

    // callId = proper call log identifier (conferenceFriendlyName / Twilio Call SID)
    const callId = safeStr(transferredContact.callId);
    const confName = safeStr(transferredContact.twilioCallSid);
    const callIdForUpdate = callId !== '' ? callId : confName;

    if (callIdForUpdate === '') {
      await logWarning('No valid call ID — skipping disposition API call', 'SessionDashboard/handleSubmitDisposition', { confName, callId }, false);
      // Reset transfer state so user is not stuck
      setShowDispositions(false);
      setIsOnTransfer(false);
      setTransferredContact(null);
      setDisposition('');
      setNotes('');
      setSdrNotes('');
      isSubmittingRef.current = false;
      return;
    }

    setIsSubmittingDisposition(true);

    try {
      const notesUrl = `${microServicesRoutes.contacts.put.contacts.updateNotes}/${transferredContact.id}`;

      await contactsApi.put(notesUrl, {
        callId: callIdForUpdate,
        notesSDR : sdrNotes
      });

      // Update call log with disposition (keep status as transferred)
      const callLogUrl = `${microServicesRoutes.contacts.put.callLog.updateCallLog}/${callIdForUpdate}`;
      await contactsApi.put(callLogUrl, {
        callId: callIdForUpdate,
        status: 'finished',
        disposition
      });

      await logInfo(t('sdrLayout.toasts.info.submitDisposition'), 'SessionDashboard/handleSubmitDisposition', {
        callIdUsed: callIdForUpdate,
        disposition,
      }, true);

      // Safety-net cleanup: executeCallCleanup may have skipped or failed to delete
      // the sdrLog (empty sdrId, hangup/delete error, polling race), leaving it orphaned.
      const sdrIdForCleanup = currentUser?.userId ?? '';
      if (sdrIdForCleanup !== '') {
        try {
          const deleteSdrLogUrl = `${microServicesRoutes.contacts.delete.sdrLogs.deleteSdrLog}/${sdrIdForCleanup}`;
          await contactsApi.delete(deleteSdrLogUrl);
        } catch (error) {
          void logError('Failed to delete SDR log after disposition', 'SessionDashboard/handleSubmitDisposition', { error, sdrId: sdrIdForCleanup }, false);
        }
      }

      // Mark conference name as processed to prevent re-detection on subsequent polls.
      // Conference names are unique per call, so this works even for re-transfers of the same contact.
      if (callId !== '') {
        processedCallIdsRef.current.add(callId);
      }
      if (confName !== '' && confName !== callId) {
        processedCallIdsRef.current.add(confName);
      }
      storeProcessedCallIds(processedCallsKey, processedCallIdsRef.current);

      // Reset transfer state
      setShowDispositions(false);
      setIsOnTransfer(false);
      setTransferredContact(null);
      setDisposition('');
      setNotes('');
      setSdrNotes('');

      // Refresh dialing status
      await fetchDialingStatus();

      // Reset processing flag
      processingTransferRef.current = false;

    } catch (error) {
      await logError(t('sdrLayout.toasts.error.submitDisposition'), 'SessionDashboard/handleSubmitDisposition', { error }, true);
    } finally {
      setIsSubmittingDisposition(false);
      isSubmittingRef.current = false;
    }
  }, [transferredContact, disposition, notes, sdrNotes, fetchDialingStatus, processedCallsKey, currentUser?.userId]);

  async function handleSDRNotesFromCard (text: string): Promise<void> {
    if ((transferredContact?.id) == null) {
      return;
    }

    const { success, error } = await saveContactNotes({
      contactId: transferredContact.id,
      callId: safeStr(transferredContact.callId),
      notesSDR: text
    });

    setSdrNotes(text);

    if (!success) {
      await logError(error?.message ?? t('sdrLayout.toasts.error.saveSdrNote'), 'AgentsPage/NotesCard.onSaveSdrNote', undefined, true);
      return;
    }
    await logInfo(t('sdrLayout.toasts.info.saveSdrNote'), 'AgentsPage/NotesCard.onSaveSdrNote', undefined, true);
  }


  // Initial fetch - only runs once
  useEffect(() => {
    if (initializedRef.current) {
      return;
    }
    if (sessionId === undefined || currentUser === null) {
      return;
    }

    const init = async (): Promise<void> => {
      setIsLoading(true);
      try {
        await fetchSession();
        await fetchDialingStatus();
        await fetchCallingNumber();
      } catch (error) {
        await logError(t('sdrLayout.toasts.error.initializeSessionDashboard'), 'SessionDashboard/init', { error }, true);
      } finally {
        setIsLoading(false);
        initializedRef.current = true;
      }
    };

    void init();
  }, [sessionId, currentUser, fetchSession, fetchDialingStatus, fetchCallingNumber]);

  // Fetch voicemail URL from voiceDropsIds after init completes
  useEffect(() => {
    if (isLoading) {
      return;
    }

    const fetchVoicemailUrl = async (): Promise<void> => {
      const voiceDropsIds = sessionRef.current?.voiceDropsIds;
      if (voiceDropsIds == null || voiceDropsIds.length === 0) {
        return;
      }
      try {
        const result = await getRecordingById(voiceDropsIds[0]);
        if (result.success && result.data?.url != null) {
          setVoicemailUrl(result.data.url);
        }
      } catch (error) {
        void logWarning('Failed to fetch voicemail URL', 'SessionDashboard/fetchVoicemailUrl', { error }, false);
      }
    };

    void fetchVoicemailUrl();
  }, [isLoading]);

  // Note: processedCallIds are NOT cleared on unmount — they must persist across
  // tab switches so old transfers are not re-detected when the user returns.
  // They are keyed by sessionId and naturally expire when the session tab closes.

  // Unified polling for dialing status - runs every 3 seconds.
  // Always runs (including during transfer) to keep contacts/notes updated in real-time.
  // Re-processing of already-handled transfers is prevented by processedCallIdsRef deduplication.
  useEffect(() => {
    if (sessionId === undefined) {
      return;
    }

    pollingRef.current = setInterval(() => {
      void fetchDialingStatus();
    }, POLL_INTERVAL);

    return () => {
      if (pollingRef.current !== null) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [fetchDialingStatus, sessionId]);

  // When conferenceName arrives after transfer was already detected (timing race),
  // update transferredContact with the conference name for BOTH callId and twilioCallSid.
  // The conferenceFriendlyName IS the call log identifier used by PUT /call-logs/:callId.
  useEffect(() => {
    if (conferenceName == null || conferenceName === '' || transferredContact === null) {
      return;
    }
    // If both fields are already populated, nothing to do
    if (transferredContact.twilioCallSid !== '' && transferredContact.callId !== '') {
      return;
    }
    setTransferredContact((prev) => {
      if (prev === null) {
        return null;
      }
      return {
        ...prev,
        callId: prev.callId !== '' ? prev.callId : conferenceName,
        twilioCallSid: prev.twilioCallSid !== '' ? prev.twilioCallSid : conferenceName,
      };
    });
  }, [conferenceName, transferredContact]);

  // Immediate transfer detection — reacts to conferenceName from Twilio store
  // instead of waiting for the next 3s poll cycle (~200-500ms vs ~3-5s)
  useEffect(() => {
    if (
      conferenceName == null ||
      conferenceName === '' ||
      isOnTransferRef.current ||
      transferredContactRef.current !== null ||
      processedCallIdsRef.current.has(conferenceName) ||
      processingTransferRef.current ||
      sessionId === undefined
    ) {
      return;
    }

    processingTransferRef.current = true;
    let cancelled = false;

    const detectTransfer = async (): Promise<void> => {
      try {
        // The Twilio leg reaches this browser in ~200-500ms, which can beat the
        // call log becoming queryable by conference name. This used to be a
        // single attempt, and losing it lost the transfer for good: the effect
        // only re-runs when conferenceName changes, so nothing ever tried again
        // and the SDR stayed on the dashboard with the caller already connected.
        const callLogUrl = `${microServicesRoutes.contacts.get.callLogs.sdrCallLog}/${conferenceName}`;
        let callLogData: CallLogSDR | null = null;

        for (let attempt = 1; attempt <= TRANSFER_LOOKUP_ATTEMPTS; attempt++) {
          if (cancelled) {
            return;
          }
          try {
            const callLogRes = await contactsApi.get<CallLogSDR>(callLogUrl);
            callLogData = callLogRes.data ?? null;
          } catch (err) {
            void logWarning(`Transfer lookup attempt ${attempt} failed`, 'SessionDashboard/immediateTransfer', {
              event: 'transfer.lookup_retry', attempt, conferenceId: conferenceName, callSessionId: sessionId, sdrId, err,
            }, false);
          }
          if (callLogData?.contact != null) {
            break;
          }
          if (attempt < TRANSFER_LOOKUP_ATTEMPTS) {
            await sleep(TRANSFER_LOOKUP_RETRY_MS);
          }
        }

        const contact = callLogData?.contact;

        if (contact == null) {
          // Surfaced rather than swallowed: the caller is on the line right now,
          // so the SDR has to know the screen is not going to switch.
          await logError(t('sdrLayout.toasts.error.fetchTransfer'), 'SessionDashboard/immediateTransfer', {
            event: 'transfer.lookup_failed', attempts: TRANSFER_LOOKUP_ATTEMPTS, conferenceId: conferenceName, callSessionId: sessionId, sdrId,
          }, true);
          return;
        }

        // Best effort. Missing notes are cosmetic, and letting this throw used to
        // take the whole transfer down with it.
        let notesAgent: string | undefined;
        let notesSDR: string | undefined;
        try {
          const contactsUrl = `${microServicesRoutes.contacts.get.contact.getById}/${safeStr(contact.id)}`;
          const contactRes = await contactsApi.get<ContactInfoDto>(contactsUrl);
          notesAgent = contactRes.data?.notesAgent;
          notesSDR = contactRes.data?.notesSDR;
        } catch (err) {
          void logWarning('Could not load contact notes for the transfer', 'SessionDashboard/immediateTransfer', { err }, false);
        }

        if (cancelled) {
          return;
        }

        const externalId =
            contact.type === 'SALESFORCE' || contact.type === 'HUBSPOT'
              ? contact.meta?.externalId ?? ''
              : '';

        isEndingCallRef.current = false;
        setIsOnTransfer(true);
        setCallActive(true);
        setTransferredContact({
          id: contact.id ?? '',
          callId: conferenceName,
          twilioCallSid: conferenceName,
          name: contact.name ?? '',
          phone: contact.phone ?? '',
          accountName: contact.accountName,
          jobTitle: contact.jobTitle,
          email: contact.email ?? '',
          notes: contact.notes ?? '',
          speech: sessionRef.current?.speech,
          scriptTitle: sessionRef.current?.scriptTitle,
          externalId,
        });

        setNotes(notesAgent ?? callLogData?.notesAgent ?? '');
        setSdrNotes(notesSDR ?? callLogData?.notesSDR ?? '');

        // Functional update: the poll rewrites this list every 3s, and the array
        // captured when the effect ran is already stale by the time the retries
        // above finish.
        setDialingContacts((prev) => prev.map(
          contactDial => contactDial.id === contact.id ? { ...contactDial, finalStatus: 'transferred' } : contactDial
        ));

        // Pause session — SDR is busy handling the transfer
        try {
          const sessionUrl = `${microServicesRoutes.contacts.put.callSession.updateNest}/${sessionId}`;
          await contactsApi.put(sessionUrl, {
            sdrStatus: 'busy',
            status: 'paused',
          });
          setIsPaused(true);
        } catch (err) {
          void logError('Could not mark the user busy for this transfer', 'SessionDashboard/immediateTransfer', {
            event: 'transfer.pause_failed', conferenceId: conferenceName, callSessionId: sessionId, sdrId, err,
          }, false);
        }

        await logInfo('Transfer received by the user', 'SessionDashboard/immediateTransfer', {
          event: 'transfer.received', conferenceId: conferenceName, callSessionId: sessionId, sdrId, contactId: contact.id,
        }, false);
      } catch (err) {
        void logError('Immediate transfer detection failed', 'SessionDashboard/immediateTransfer', { err }, false);
      } finally {
        // Always released. Re-entry is already prevented by the isOnTransfer and
        // transferredContact guards above, so holding this past the lookup only
        // ever served to wedge the next transfer.
        processingTransferRef.current = false;
      }
    };

    void detectTransfer();

    return () => {
      cancelled = true;
    };
  }, [conferenceName, sessionId]);

  // Conference polling - check if conference is still active when SDR is on a transferred call
  // This detects when client hangs up and conference ends
  useEffect(() => {
    const checkConferenceParticipants = async (): Promise<void> => {
      if (isEndingCallRef.current) {
        return;
      }

      const callLogId = transferredContact?.callId ?? '';
      const twilioSid = transferredContact?.twilioCallSid;
      const sdrIdValue = currentUser?.userId;

      if (twilioSid === undefined || twilioSid === '' || sdrIdValue === undefined) {
        return;
      }

      const endTransfer = async (reason: string, context: Record<string, unknown>): Promise<void> => {
        if (isEndingCallRef.current) {
          return;
        }
        isEndingCallRef.current = true;

        if (conferencePollingRef.current !== null) {
          clearInterval(conferencePollingRef.current);
          conferencePollingRef.current = null;
        }

        await executeCallCleanup(callLogId, twilioSid, sdrIdValue);
        await logInfo(reason, 'SessionDashboard/conferencePolling', {
          event: 'transfer.ended', conferenceId: twilioSid, callSessionId: sessionId, sdrId: sdrIdValue, ...context,
        }, false);
      };

      try {
        const participantsUrl = `${microServicesRoutes.edge.get.twilio.participants}/${twilioSid}/${sdrIdValue}`;
        const response = await edgeApi.get<number>(participantsUrl);
        const participantCount = response.data;

        conferenceCheckFailuresRef.current = 0;

        // If 0 or 1 participants, conference has ended (client/agent hung up)
        if (participantCount <= 1) {
          await endTransfer('Conference ended by remote participant', { twilioSid, participantCount });
        }
      } catch (error) {
        // A failed check is NOT proof the conference ended. This used to tear the
        // transfer down on any error at all, so a single 500, timeout or token
        // refresh dropped a live call.
        //
        // 404 is not treated as definitive either: edge answers NotFound while the
        // conference is still being built, and the immediate check below fires the
        // instant the transfer starts — so trusting it killed transfers at the
        // exact moment they began. A client who really hung up shows up as
        // `participantCount <= 1` on a 200, which still ends the call at once;
        // only the abnormal path waits.
        conferenceCheckFailuresRef.current += 1;

        const status = (error as { response?: { status?: number } })?.response?.status;

        if (conferenceCheckFailuresRef.current < CONFERENCE_CHECK_FAILURES_BEFORE_END) {
          void logWarning(
            `Conference check failed (${conferenceCheckFailuresRef.current}/${CONFERENCE_CHECK_FAILURES_BEFORE_END})`,
            'SessionDashboard/conferencePolling',
            { event: 'transfer.conference_check_failed', conferenceId: twilioSid, callSessionId: sessionId, sdrId: sdrIdValue, httpStatus: status },
            false
          );
          return;
        }

        await endTransfer('Conference unreachable on consecutive checks', { twilioSid, status, failures: conferenceCheckFailuresRef.current });
      }
    };

    // Only poll when on a transfer and call is active
    if (isOnTransfer && callActive && transferredContact !== null) {
      conferenceCheckFailuresRef.current = 0;
      // Check immediately
      void checkConferenceParticipants();

      // Then poll every 3 seconds
      conferencePollingRef.current = setInterval(() => {
        void checkConferenceParticipants();
      }, 3000);
    }

    return () => {
      if (conferencePollingRef.current !== null) {
        clearInterval(conferencePollingRef.current);
        conferencePollingRef.current = null;
      }
    };
  }, [isOnTransfer, callActive, transferredContact, currentUser?.userId, executeCallCleanup]);

  if (isLoading) {
    return (
      <div className="bg-background flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  // Transfer/Dialer view
  if (isOnTransfer && transferredContact !== null) {
    return (
      <div className="bg-background">
        <div className="mx-auto space-y-4">
          <h1 className="text-2xl font-bold">{t('wizard.dashboard.transferTitle')}</h1>

          <ContactCard
            id={transferredContact.id}
            externalId={transferredContact.externalId}
            name={transferredContact.name}
            jobTitle={transferredContact.jobTitle ?? ''}
            email={transferredContact.email ?? ''}
            phone={transferredContact.phone}
            account={transferredContact.accountName ?? ''}
            showFuegoLead={false}
            isTransferred={true}
            activeVoicemail={isOnVoicemail}
            isSDR={true}
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <IntentCard
              speech={transferredContact.speech}
              title={transferredContact.scriptTitle}
              isTransferred={true}
              activeVoicemail={isOnVoicemail}
              isSDR={true}
            />

            <Dialpad
              number={transferredContact.phone}
              active={callActive}
              onCallEnd={() => {
                void handleCallEnd();
              }}
              muted={muted}
              onToggleMute={(next) => {
                void handleToggleMute(next);
              }}
              hasVoicemail={typeof voicemailUrl === 'string' && voicemailUrl.trim() !== ''}
              onPlayVoicemail={callActive ? () => {
                void handlePlayVoicemail();
              } : undefined}
              isTransferred={true}
              activeVoicemail={isOnVoicemail}
              showTransfer={false}
              isSDR={true}
            />

            <NotesCard
              contactId={transferredContact.id}
              note={transferredContact.notes}
              agentNote={notes}
              sdrNote={sdrNotes}
              isSDR={true}
              onSaveSdrNote={handleSDRNotesFromCard}
              onSaveAgentNote={undefined}
              isTransferred={true}
              activeVoicemail={isOnVoicemail}
            />
          </div>
        </div>

        <DispositionsModal
          open={showDispositions}
          onOpenChange={setShowDispositions}
          options={DISPOSITION_OPTIONS}
          value={disposition}
          onChange={setDisposition}
          agentNotes={notes}
          onNotesChange={setNotes}
          showAgentNotes={false}
          showSdrNotes={true}
          sdrNotes={sdrNotes}
          onSdrNotesChange={setSdrNotes}
          submitDisabled={isSubmittingDisposition}
          onSubmit={() => {
            void handleSubmitDisposition();
          }}
          showFuegoLead={false}
        />
      </div>
    );
  }

  // Dashboard view
  return (
    <div className="bg-background">
      <div key={dialerKey} className="mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold">{t('wizard.dashboard.title')}</h1>
          <p className="text-muted-foreground">{t('wizard.dashboard.description')}</p>
        </div>

        {isPaused && (
          <SessionPausedBanner/>
        )}

        <SessionHeader
          queueCount={queueLength}
          phoneNumber={callingFromNumber}
          isPaused={isPaused}
          onPause={() => {
            void handlePause();
          }}
          onResume={() => {
            void handleResume();
          }}
          onEnd={() => {
            setIsOpenDialog(true);
          }}
          isEnding={isEnding}
        />

        <SessionContactsTable contacts={dialingContacts}/>
      </div>

      <ConfirmEndSessionDialog
        open={isOpenDialog}
        onCancel={() => {
          setIsOpenDialog(false);
        }}
        onConfirm={() => {
          void handleEndSession();
        }}
      />
    </div>
  );
}
